import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { Refresh as RefreshIcon, Error as ErrorIcon } from '@mui/icons-material';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';

/**
 * Props for the TreeErrorBoundary component
 */
interface TreeErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
}

/**
 * State for the TreeErrorBoundary component
 */
interface TreeErrorBoundaryState {
  /** Whether an error has occurred */
  hasError: boolean;
  /** The error that occurred */
  error: Error | null;
  /** The error info from React */
  errorInfo: ErrorInfo | null;
}

/**
 * TreeErrorBoundary component catches errors in the tree components
 * and displays a user-friendly error message with a retry option.
 */
export class TreeErrorBoundary extends Component<TreeErrorBoundaryProps, TreeErrorBoundaryState> {
  constructor(props: TreeErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Derive error state from error that occurred
   */
  static getDerivedStateFromError(error: Error): Partial<TreeErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Handle component errors - only used for logging
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    log.error('[TreeErrorBoundary] Tree error caught:', {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });
  }

  /**
   * Reset error state to try again
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (!hasError) {
      return children;
    }

    return (
      <Box
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 3,
            maxWidth: 400,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <ErrorIcon
            color="error"
            sx={{
              fontSize: 48,
              mb: 2,
            }}
          />

          <Typography variant="h6" gutterBottom>
            Something went wrong
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
            {formatErrorMessage(error, 'An unexpected error occurred in the tree view.')}
          </Typography>

          <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRetry}>
            Try Again
          </Button>
        </Paper>
      </Box>
    );
  }
}
